/* eslint-disable no-useless-escape */
/**
 * Collection of functions to help fix an incorrect route
 */
import url from 'url';
import { get, isEmpty } from 'lodash';
import { getRoutePartWithSlug } from './common';
import { Router, i18n } from '../src/i18n';

/**
 * Test if entity slug-id is equal to provided in object
 *
 * @param {Object} entity Data source
 * @param {Object} queryValues Query object
 * @returns {boolean}
 */
export const testSlugId = (entity, queryValues) => {
  const queryId = get(queryValues, 'id');
  const entityId = getRoutePartWithSlug(entity);

  return queryId === entityId;
};

/**
 * Test if entity slug-id and country are equal to provided in object
 *
 * @param {Object} entity Data source
 * @param {Object} queryValues Query object
 * @returns {boolean}
 */
export const testCountryAndSlugId = (entity, queryValues) => {
  const queryCountry = get(queryValues, 'country');
  const queryId = get(queryValues, 'id');

  const entityId = getRoutePartWithSlug(entity);
  const entityCountry = get(entity, 'country.slug');

  return queryCountry === entityCountry && queryId === entityId;
};

/**
 * Test if entity slug-id, year and country are equal to provided in object
 *
 * @param {Object} entity Data source
 * @param {Object} queryValues Query object
 * @returns {boolean}
 */
export const testYearAndCountryAndSlugId = (entity, queryValues) => {
  const queryYear = get(queryValues, 'year');
  const queryCountry = get(queryValues, 'country');
  const queryId = get(queryValues, 'id');

  const entityId = getRoutePartWithSlug(entity);
  const entityYear = '' + get(entity, 'year');
  const entityCountry = get(entity, 'country.slug');

  return queryYear === entityYear && queryId === entityId && queryCountry === entityCountry;
};

/**
 * Test if entity slug-id, year and country are equal to provided in object
 *
 * @param {Object} entity Data source
 * @param {Object} queryValues Query object
 * @returns {boolean}
 */
export const testYearAndCountry = (entity, queryValues) => {
  const queryYear = get(queryValues, 'year');
  const queryCountry = get(queryValues, 'country');

  const entityYear = '' + get(entity, 'year');
  const entityCountry = get(entity, 'country.slug');

  return queryYear === entityYear && queryCountry === entityCountry;
};

/**
 * Takes slug-id from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @returns {Object} {string} Fixed asPath
 */
export const fixSlugId = (entity, pathname, asPath, query) => {
  if (!get(entity, 'id') || !get(entity, 'slug')) {
    throw new Error('Entity should contain id and slug');
  }

  const parsedAs = url.parse(asPath);
  const correctId = getRoutePartWithSlug(entity);
  const fixedAs = matchPathnameToAs(pathname, parsedAs.pathname, { id: correctId });
  const updatedQuery = { ...query };
  delete updatedQuery.id;
  return url.format({ pathname: fixedAs, query: updatedQuery });
};

/**
 * Takes composer slug/slug-id from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @returns {Object} {string} Fixed asPath
 */
export const fixComposerAndSlugId = (entity, pathname, asPath) => {
  if (!get(entity, 'id') || !get(entity, 'slug')) {
    throw new Error('Entity should contain id and slug');
  }

  const parsedAs = url.parse(asPath);
  const correctId = `${get(entity, 'composers[0].slug')}-${getRoutePartWithSlug(entity)}`;
  const fixedAs = matchPathnameToAs(pathname, parsedAs.pathname, { id: correctId });

  return url.format(fixedAs);
};

/**
 * Takes country/slug-id from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @returns {Object} {string} Fixed asPath
 */
export const fixCountryAndSlugId = (entity, pathname, asPath) => {
  if (!get(entity, 'id') || !get(entity, 'slug') || !get(entity, 'country')) {
    throw new Error('Entity should contain id and slug');
  }

  const parsedAs = url.parse(asPath);
  const correctId = getRoutePartWithSlug(entity);
  const fixedAs = matchPathnameToAs(pathname, parsedAs.pathname, {
    id: correctId,
    country: get(entity, 'country.slug', ''),
  });

  return url.format(fixedAs);
};

/**
 * Takes year/country/slug-id from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @returns {Object} {string} Fixed asPath
 */
export const fixYearAndCountryAndSlugId = (entity, pathname, asPath, query) => {
  if (!get(entity, 'id') || !get(entity, 'slug') || !get(entity, 'year') || !get(entity, 'country')) {
    throw new Error('Entity should contain id and slug');
  }

  const parsedAs = url.parse(asPath);
  const correctId = getRoutePartWithSlug(entity);
  const fixedAs = matchPathnameToAs(pathname, parsedAs.pathname, {
    id: correctId,
    year: get(query, 'year', ''),
    country: get(entity, 'country.slug', ''),
  });

  return url.format(fixedAs);
};

/**
 * Takes year/country/slug-id from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @returns {Object} {string} Fixed asPath
 */
export const fixYearAndCountry = (entity, pathname, asPath) => {
  if (!get(entity, 'year') || !get(entity, 'country')) {
    throw new Error('Entity should contain id and slug');
  }

  const parsedAs = url.parse(asPath);
  const fixedAs = matchPathnameToAs(pathname, parsedAs.pathname, {
    year: get(entity, 'year', ''),
    country: get(entity, 'country.slug', ''),
  });

  return url.format(fixedAs);
};

/**
 * Match pathame patter and insert corrected values
 *
 * @param {String} pathname Router pathname
 * @param {String} asToFix Router asPath
 * @param {Object} query Values to fix
 * @returns {String}
 */
export const matchPathnameToAs = (pathname, asToFix, query) => {
  const pathnameArray = pathname.replace(/\/$/, '').split('/');
  const asToFixArray = asToFix.replace(/\/$/, '').split('/');
  if (pathnameArray.length > asToFixArray.length) {
    throw new Error('The pathname contains less parameters than the as provided');
  }

  const fixedAsArray = pathnameArray.map((item, key) => {
    // noinspection RegExpRedundantEscape
    if (/^\[.*\]$/.test(item)) {
      const cleanItem = item.replace(/[\[\]]/g, '');
      return get(query, cleanItem, asToFixArray[key]);
    }
    return asToFixArray[key];
  });
  return fixedAsArray.join('/');
};

/**
 * Check tab value exist or not
 *
 * @param {Object} query Values to fix
 * @param {Object} tabValues list of possible tab values
 * @returns {Object} if isTabValueExist then return tabValue
 */

export const fixTabValues = (query, tabValues) => {
  let tabValue;
  let isTabValueExist;
  if (query?.action?.length) {
    const index = query.action.indexOf(i18n.language);
    if (index > -1) {
      query.action.splice(index, 1);
    }

    isTabValueExist = query?.action?.length && Object.values(tabValues).includes(query.action[0]);
    tabValue = isTabValueExist ? query.action[0] : undefined;
  }
  return { tabValue, isTabValueExist };
};

/**
 * Idea is to run test function and fix function if needed
 * Returns error code or redirect if it possible
 *
 * @param {Object} entity Source data entity
 * @param {Object} params Router, context and redirect status
 * @param {Function} test Function to test route
 * @param {Function} fix Function to fix route
 * @returns {null|number} Returns error code or null
 */
export function routeValidation(
  entity,
  params,
  test = testSlugId,
  fix = fixSlugId,
  tabValues,
  tabValidation = fixTabValues,
  redirectRule,
) {
  if (isEmpty(entity)) {
    return 404;
  }
  const { router, ctx, status = redirectRule || 301 } = params;
  const { query, asPath, pathname } = router;
  const testResult = test(entity, query);

  const { tabValue, isTabValueExist } = tabValidation(query, tabValues);

  try {
    if (!testResult || (!isTabValueExist && query?.action?.length)) {
      const fixedAsPath = fix(entity, pathname, asPath, query, tabValue);
      const regExp = new RegExp(`^${fixedAsPath}(/\\w{2})?$`, 'igm');
      if (regExp.test(asPath) === false) {
        if (ctx.res) {
          // Seems to be the version used by zeit
          ctx.res.writeHead(status, {
            Location: fixedAsPath,
            'Content-Type': 'text/html; charset=utf-8',
          });
          ctx.res.end();
        }
        Router.replace(pathname, fixedAsPath, { shallow: true });
      } else {
        return 404;
      }
    }
  } catch (e) {
    return 404;
  }

  return null;
}

export const tabRouteValidation = (action, tabValues) => {
  if (!action) {
    return tabValues[Object.keys(tabValues)[0]];
  }
  const index = action.indexOf(i18n.language);
  if (index > -1) {
    action.splice(index, 1);
  }

  for (const [key, value] of Object.entries(tabValues)) {
    if (value === action[0]) {
      return action[0];
    }
  }

  return tabValues[Object.keys(tabValues)[0]];
};

export const validateProductionStubRoute = (action, tabValues, lang) => {
  if (!action) {
    return [tabValues[Object.keys(tabValues)[0]]];
  }

  const index = action.indexOf(lang);
  if (index > -1) {
    action.splice(index, 1);
  }

  if (action.length <= 1) {
    for (const [key, value] of Object.entries(tabValues)) {
      if (value === action[0]) {
        return [action[0]];
      }
    }
  } else if (action.length > 1 && action[0] === tabValues.PERFORMANCES) {
    return [action[0], action[1]];
  }
  return [tabValues[Object.keys(tabValues)[0]]];
};

/**
 * Match pathame patter and insert corrected values
 *
 * @param {String} pathname Router pathname
 * @param {String} asToFix Router asPath
 * @param {Object} query Values to fix
 * @returns {String}
 */
export const matchTabPathnameToAs = (pathname, asToFix, query) => {
  const pathnameArray = pathname.replace(/\/$/, '').split('/');
  const asToFixArray = asToFix.replace(/\/$/, '').split('/');

  const fixedAsArray = pathnameArray.map((item, key) => {
    // noinspection RegExpRedundantEscape
    if (/^\[.*\]$/.test(item)) {
      const cleanItem = item.replace(/[\.[\]]/g, '');

      if (item === '[[...action]]') {
        return get(query, cleanItem, undefined);
      }
      return get(query, cleanItem, asToFixArray[key]);
    }
    return asToFixArray[key];
  });
  return fixedAsArray.join('/');
};

/**
 * Takes slug-id and action from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @param {Object} tabResult Router tabValue
 * @returns {Object} {string} Fixed asPath
 */
export const fixTabAndSlugId = (entity, pathname, asPath, query, tabResult) => {
  if (!get(entity, 'id') || !get(entity, 'slug')) {
    throw new Error('Entity should contain id and slug');
  }
  const parsedAs = url.parse(asPath);
  const correctId = getRoutePartWithSlug(entity);
  const fixedAs = matchTabPathnameToAs(pathname, parsedAs.pathname, {
    id: correctId,
    action: tabResult,
  });

  return url.format(fixedAs);
};

/**
 * Takes country/slug-id from entity and tabvalue from qury and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @param {Object} tabResult Router tabValue
 * @returns {Object} {string} Fixed asPath
 */
export const fixTabCountryAndSlugId = (entity, pathname, asPath, query, tabResult) => {
  if (!get(entity, 'id') || !get(entity, 'slug') || !get(entity, 'country')) {
    throw new Error('Entity should contain id and slug');
  }

  const parsedAs = url.parse(asPath);
  const correctId = getRoutePartWithSlug(entity);
  const fixedAs = matchTabPathnameToAs(pathname, parsedAs.pathname, {
    id: correctId,
    country: get(entity, 'country.slug', ''),
    action: tabResult,
  });

  return url.format(fixedAs);
};

/**
 * Takes year/country/slug-id from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @returns {Object} {string} Fixed asPath
 */
export const fixTabYearAndCountryAndSlugId = (entity, pathname, asPath, query, tabResult) => {
  if (!get(entity, 'id') || !get(entity, 'slug') || !get(entity, 'country')) {
    throw new Error('Entity should contain id and slug');
  }
  const parsedAs = url.parse(asPath);
  const correctId = getRoutePartWithSlug(entity);
  const fixedAs = matchTabPathnameToAs(pathname, parsedAs.pathname, {
    id: correctId,
    year: get(query, 'year', ''),
    country: get(entity, 'country.slug', ''),
    action: tabResult,
  });
  return url.format(fixedAs);
};

/**
 * Test if entity id and production slug is equal to provided in object
 *
 * @param {Object} entity Data source
 * @param {Object} queryValues Query object
 * @returns {boolean}
 */
export const testRentalsSlugId = (entity, queryValues) => {
  const queryId = get(queryValues, 'id');
  const entityId = `${get(entity, 'production.slug')}-${entity.id}`;

  return queryId === entityId;
};

/**
 * Takes Rentals id & production slug  from entity and inserts it into asPath
 *
 * @param {Object} entity Data source
 * @param {String} pathname Router pathname
 * @param {String} asPath Router asPath
 * @returns {Object} {string} Fixed asPath
 */
export const fixRentalAndSlugId = (entity, pathname, asPath) => {
  if (!get(entity, 'id') || !get(entity, 'production.slug')) {
    throw new Error('Entity should contain id and slug');
  }

  const parsedAs = url.parse(asPath);
  const correctId = `${get(entity, 'production.slug')}-${entity.id}`;
  const fixedAs = matchPathnameToAs(pathname, parsedAs.pathname, { id: correctId });
  return url.format(fixedAs);
};
